/**
 * PaymentReturn — Nexus v29
 *
 * Handles the Stripe redirect back into the app.
 *   /billing/return?checkout=success&session_id=cs_...&return_to=/workspaces/x
 *   /billing/return?checkout=cancel&return_to=...
 *
 * On success:
 *   - Polls /api/billing/checkout/status/{session_id} until state='completed'
 *     (webhook usually arrives within 2-6 s; we also fall back to reading
 *     the balance directly).
 *   - Renders the success modal with the "Continuar construyendo" CTA.
 *   - On click → navigates to `return_to` and refreshes the global balance.
 *
 * On cancel:
 *   - Renders the failed modal with "Intentar nuevamente" (re-opens Stripe)
 *     and "Cancelar" (goes back to return_to).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Sparkles } from 'lucide-react';
import { apiService } from '../services/api';
import { useCredits } from '../contexts/CreditsContext';

type State = 'polling' | 'success' | 'cancel' | 'error';

const POLL_MAX_ATTEMPTS = 15;   // ~30 seconds max wait
const POLL_INTERVAL_MS = 2000;

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useCredits();
  const checkoutParam = params.get('checkout');
  const sessionId = params.get('session_id') || '';
  const returnTo = params.get('return_to') || '/dashboard';
  const initialState: State = checkoutParam === 'cancel'
    ? 'cancel'
    : (sessionId ? 'polling' : 'error');
  const [state, setState] = useState<State>(initialState);
  const [creditsGained, setCreditsGained] = useState<number>(0);
  const attemptsRef = useRef(0);

  // Sprint Arquitectura Definitiva #14 · Sync-first flow.
  //
  // Instead of polling until the webhook lands, we immediately POST to
  // /billing/checkout/sync/{session_id}. That endpoint:
  //   - Confirms with Stripe (live) that the session was paid.
  //   - Applies credits INSTANTLY (idempotent — the eventual webhook
  //     is a no-op thanks to stripe_events dedup).
  // If sync returns 'pending' (rare — payment truly not yet completed
  // on Stripe's side), we fall back to the classic status polling.
  useEffect(() => {
    if (state !== 'polling') return;
    let cancelled = false;
    const trySync = async () => {
      if (cancelled) return;
      try {
        const r = await apiService.syncCheckout(sessionId);
        if (r.status === 'processed' || r.status === 'already_completed' || r.status === 'already_processed') {
          setCreditsGained(r.credits_added || 0);
          setState('success');
          void refresh();
          return;
        }
        // status === 'pending' → fall back to polling.
        void pollStatus();
      } catch {
        // Any error → fall back to polling.
        void pollStatus();
      }
    };
    const pollStatus = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const s = await apiService.getCheckoutStatus(sessionId);
        if (s.state === 'completed') {
          setCreditsGained(s.credits);
          setState('success');
          void refresh();
          return;
        }
        if (s.state === 'failed') {
          setState('cancel');
          return;
        }
      } catch { /* ignore transient */ }
      if (attemptsRef.current >= POLL_MAX_ATTEMPTS) {
        setState('success');
        void refresh();
        return;
      }
      window.setTimeout(pollStatus, POLL_INTERVAL_MS);
    };
    void trySync();
    return () => { cancelled = true; };
  }, [state, sessionId, refresh]);

  const handleContinue = useCallback(() => {
    void refresh();
    nav(returnTo || '/dashboard', { replace: true });
  }, [nav, refresh, returnTo]);

  const handleRetry = useCallback(() => {
    // Re-open Stripe with a fresh session. We route back to the panel
    // (opens as inline page) to let the user pick the same or another
    // package. Keeping this simple; the panel remembers selection state
    // via the featured default.
    nav(`/billing?return_to=${encodeURIComponent(returnTo)}`, { replace: true });
  }, [nav, returnTo]);

  const handleCancel = useCallback(() => {
    nav(returnTo || '/dashboard', { replace: true });
  }, [nav, returnTo]);

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {state === 'polling' && <PollingCard />}
        {state === 'success' && (
          <SuccessCard credits={creditsGained} onContinue={handleContinue} />
        )}
        {state === 'cancel' && (
          <FailedCard onRetry={handleRetry} onCancel={handleCancel} />
        )}
        {state === 'error' && (
          <FailedCard
            onRetry={handleRetry}
            onCancel={handleCancel}
            title="No pudimos verificar el pago"
            body="La sesión de pago no fue encontrada. Por favor intenta nuevamente."
          />
        )}
      </div>
    </div>
  );
}

const PollingCard: React.FC = () => (
  <div
    data-testid="payment-polling-card"
    className="rounded-2xl border border-cyan-400/25 bg-slate-950/60 backdrop-blur-xl p-8 text-center"
  >
    <div className="mx-auto h-14 w-14 rounded-full bg-cyan-400/10 flex items-center justify-center mb-5">
      <Loader2 size={28} className="text-cyan-300 animate-spin" />
    </div>
    <div className="text-xl font-semibold text-slate-100">
      Confirmando tu pago…
    </div>
    <div className="mt-2 text-sm text-slate-400">
      Estamos activando tus créditos. Esto toma solo un momento.
    </div>
  </div>
);

const SuccessCard: React.FC<{ credits: number; onContinue: () => void }> = ({
  credits, onContinue,
}) => (
  <div
    data-testid="payment-success-modal"
    className="rounded-2xl border border-cyan-400/40 bg-slate-950/70 backdrop-blur-xl p-8 text-center relative overflow-hidden"
  >
    <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl pointer-events-none" />
    <div className="absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />

    <div className="relative mx-auto h-16 w-16 rounded-full bg-cyan-400/15 border border-cyan-400/40 flex items-center justify-center mb-6">
      <CheckCircle2 size={34} className="text-cyan-300" strokeWidth={2} />
      <div className="absolute inset-0 rounded-full ring-2 ring-cyan-400/30 animate-ping" />
    </div>

    <div className="text-2xl font-semibold text-slate-100">
      Pago realizado con éxito
    </div>
    <div className="mt-2 text-sm text-slate-400">
      Tus créditos ya están disponibles.
      {credits > 0 && (
        <span className="block mt-1 text-cyan-300">
          <Sparkles size={12} className="inline mr-1" />
          +{credits.toLocaleString('es-ES')} créditos acreditados
        </span>
      )}
    </div>

    <button
      type="button"
      onClick={onContinue}
      data-testid="continue-building-button"
      className="mt-7 w-full rounded-xl bg-gradient-to-r from-cyan-400 to-cyan-300 text-slate-950 font-semibold py-3.5 shadow-[0_20px_50px_-15px_rgba(34,211,238,0.6)] hover:from-cyan-300 hover:to-cyan-200 transition-colors"
    >
      Continuar construyendo →
    </button>
  </div>
);

const FailedCard: React.FC<{
  onRetry: () => void;
  onCancel: () => void;
  title?: string;
  body?: string;
}> = ({ onRetry, onCancel, title, body }) => (
  <div
    data-testid="payment-failed-modal"
    className="rounded-2xl border border-rose-400/30 bg-slate-950/70 backdrop-blur-xl p-8 text-center"
  >
    <div className="mx-auto h-14 w-14 rounded-full bg-rose-500/10 border border-rose-500/40 flex items-center justify-center mb-5">
      <XCircle size={30} className="text-rose-400" />
    </div>
    <div className="text-xl font-semibold text-slate-100">
      {title || 'Pago rechazado'}
    </div>
    <div className="mt-2 text-sm text-slate-400">
      {body || 'No pudimos procesar tu tarjeta. Puedes intentar nuevamente con otro método de pago.'}
    </div>

    <div className="mt-6 flex flex-col gap-2">
      <button
        type="button"
        onClick={onRetry}
        data-testid="payment-retry-button"
        className="w-full rounded-xl bg-gradient-to-r from-cyan-400 to-cyan-300 text-slate-950 font-semibold py-3.5 hover:from-cyan-300 hover:to-cyan-200 transition-colors"
      >
        Intentar nuevamente
      </button>
      <button
        type="button"
        onClick={onCancel}
        data-testid="payment-cancel-button"
        className="w-full rounded-xl border border-slate-800 hover:border-slate-700 text-slate-300 py-3"
      >
        Cancelar
      </button>
    </div>
  </div>
);
